<template>
    <div class="dw-10 f fh fj-sb b-b ath-table-header"
        :class="[hasSuperHeaders ? 'ath-table-header-below-supers' : '']">
        <div v-for="column in uiHeaders"
            class="ath-header-cell sp-l-s"
            :class="[
                column.locked ? 'ath-lock-column' : '',
                column.left_locked ? 'ath-left-lock-column' : '',
                column.right_locked ? 'ath-right-lock-column' : '',
                column.align === 'right' ? 'ath-align-right sp-r-s' : '',
                column.align === 'center' ? 'ath-align-center' : ''
            ]"
            :style="{
                'width': column.width_rem + 'rem',
                'left': column.left_position + 'rem',
                'right': column.right_position + 'rem'
            }"
            :title="column.description ? column.description : ''"
            :key="column.uid">
            <AthSort v-if="column.sort_enabled"
                class="ath-header-cell-item"
                :pubkey="sortUid"
                :sort="column.key"
                :type="column.sort_type"
                :uiActive="column.sort_active"
                :uiTitle="column.title"
                :uiClass="column.text_classes">
                <VisualDot v-if="column.visual === 'dot'" v-bind="column.visual_props" class="sm-r-xs" />
                <i v-if="column.visual === 'icon'" v-bind="column.visual_props" class="sm-r-xs" aria-hidden="true"></i>
                <template v-if="column.visual === 'image'">
                    <span class="sm-r-xs" v-if="column.visual_props.lbl">{{ column.visual_props.lbl }}</span>
                    <img v-bind="column.visual_props" aria-hidden="true" />
                </template>
                <template #before-dir>
                    <AthButton v-if="column.info_desc" class="el--s-xs sm-l-xs dw-a" lbl="?" :title="column.info_desc" />
                </template>
            </AthSort>
            <div v-else
                class="ath-header-cell-item f fa-c fh dw-10"
                :class="column.text_classes">
                <TruncatedTitle v-if="column.title" class="fas-c" :uiTitle="column.title" />
                <VisualDot v-if="column.visual === 'dot'" v-bind="column.visual_props" class="sm-r-xs" />
                <i v-if="column.visual === 'icon'" v-bind="column.visual_props" class="sm-r-xs" aria-hidden="true"></i>
                <template v-if="column.visual === 'image'">
                    <span class="sm-r-xs" v-if="column.visual_props.lbl">{{ column.visual_props.lbl }}</span>
                    <img v-bind="column.visual_props" aria-hidden="true" />
                </template>
                <AthButton v-if="column.info_desc" class="el--s-xs sm-l-xs dw-a" lbl="?" :title="column.info_desc" />
                <Badge v-if="column.badge_text" :lbl="column.badge_text" />
            </div>
        </div>
    </div>
</template>

<script>
import AthSort from '../../atoms/Sort.vue'
import TruncatedTitle from '../../atoms/visuals/TruncatedTitle.vue';
import VisualDot from '../../atoms/visuals/Dot.vue';
import AthButton from '../../atoms/Button.vue';
import Badge from '../../atoms/visuals/Badge.vue'

export default {
    name: 'mead-table-header',
    props: {
        /**
         * The column definitions to render as header cells, including title, width, alignment,
         * lock/sort configuration, and optional visual/badge/info options.
         */
        columns: {type: Array, default: () => []},
        /**
         * Optional super-header definitions rendered above the column headers; when present,
         * the header row is offset to sit below them.
         */
        superHeaders: {type: Array, default: () => []},
        /**
         * The pubkey passed to each sortable column's AthSort control, used to coordinate which
         * column is actively sorted.
         */
        sortUid: {type: String, default: ''},
    },
    components: {AthSort, TruncatedTitle, VisualDot, AthButton, Badge},
    computed: {
        /**
         * Returns true if we have super headers defined
         */
        hasSuperHeaders: function () {
            return this.superHeaders.length > 0;
        },
        /**
         * Number of locked headers from the left side of the row
         */
        nrOfLockedLeftHeaders: function () {
            let nr = 0;

            for (const header of this.columns) {
                if (!header.locked) break;

                nr++;
            }

            return nr;
        },
        /**
         * Number of locked headers from the right side of the row
         */
        nrOfLockedRightHeaders: function () {
            let nr = 0;

            const columns = [...this.columns].reverse();
            for (const header of columns) {
                if (!header.locked) break;

                nr++;
            }

            return nr;
        },
        /**
         * Headers that are locked in the beginning of the row
         */
        leftLockedHeaders: function () {
            const list = [];

            if (this.nrOfLockedLeftHeaders === 0) return list;

            for (let i = 0; i < this.nrOfLockedLeftHeaders; i++) {
                let leftPosition = 0;

                for (const el of list) {
                    leftPosition += el.width_rem;
                }

                // Add the base header into the list
                list.push(Object.assign({}, this.columns[i], {left_position: leftPosition, left_locked: true}));
            }

            return list;
        },
        /**
         * Build a new set of headers that includes the left positioning for sticky columns
         */
        computedHeaders: function () {
            const list = [];
            for (let i = this.nrOfLockedLeftHeaders; i < this.columns.length - this.nrOfLockedRightHeaders; i++) {
                let leftPosition = 0;
                let locked = this.columns[i].locked;

                for (const el of list) {
                    leftPosition += el.width_rem;

                    // If header is locked but not all previous headers are set this one to also not be locked
                    if (this.columns[i].locked && !el.locked) locked = false;
                }

                // Add the base header into the list
                list.push(Object.assign({}, this.columns[i], {locked, left_position: leftPosition}));
            }

            return list;
        },
        /**
         * Headers that are locked at the end of the row
         */
        rightLockedHeaders: function () {
            const list = [];

            if (this.nrOfLockedRightHeaders === 0) return list;

            for (let i = this.columns.length - 1; i >= this.columns.length - this.nrOfLockedRightHeaders; i--) {
                let rightPosition = 0;

                for (const el of list) {
                    rightPosition += el.width_rem;
                }

                // Add the base header into the list
                list.push(Object.assign({}, this.columns[i], {right_position: rightPosition, right_locked: true}));
            }

            return list.reverse();
        },
        uiHeaders: function () {
            return [...this.leftLockedHeaders, ...this.computedHeaders, ...this.rightLockedHeaders];
        },
    },
};
</script>
<style lang="scss" scoped>
@use '../../../../sass/variables' as *;
// Table header style
.ath-table-header {
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: var(--c-panel-dark-bg);

    &.ath-table-header-below-supers {
        top: 4rem;
    }

    .ath-header-cell-item {
        min-height: 4rem;
    }

    .ath-align-right {
        .ath-header-cell-item {
            justify-content: flex-end;
        }
    }

    .ath-align-center {
        .ath-header-cell-item {
            justify-content: center;
        }
    }

    // Default sizing for cells. Needed so that smaller widths don't collapse the cells
    .ath-header-cell {
        min-width: 7rem;

        // Ensure child elements don't interfere with the parent's title tooltip
        // when description is set
        &[title]:not([title=""]) {
            * {
                pointer-events: none;
            }
        }
    }
}
</style>